/**
 * Spectacle Scorer — 观赏性评分（纯本地计算，不消耗 LLM）
 *
 * 从战斗逐 tick 事件流提取 6 维度指标，加权汇总为 0-100 分。
 * 用于 Evolution Arena 的 fitness 评分（权重 25%）和跨代观赏性趋势追踪。
 *
 * 6 维度:
 *   1. 伤害交换率 (25%) — 双方都有伤害输出 → 高分；一方碾压 → 低分
 *   2. 移动复杂度 (20%) — 移动次数 + 位置多样性
 *   3. 掩体交互  (15%) — COVER_USE 事件次数
 *   4. 阶段推进  (15%) — HP 从满 → 中 → 低 → 结束，阶段越多越好
 *   5. 时长最优  (15%) — 200-800 ticks 黄金区间，太短/太长扣分
 *   6. 濒死翻盘  (10%) — HP<30% 后存活或翻盘
 */

// ===== 输入类型（结构化兼容 evolution-arena.ts 的 ReplayFrame）=====

export interface SpectacleFrame {
  tick: number;
  selfHp: number;
  enemyHp: number;
  selfAction: string;
  enemyAction: string;
  damageAtoB: number;
  damageBtoA: number;
  selfPos: { x: number; y: number };
  enemyPos: { x: number; y: number };
}

export interface SpectacleKeyEvent {
  eventType: string;
  tickNum: number;
  actor: string;
}

export interface SpectacleInput {
  frames: SpectacleFrame[];
  totalTicks: number;
  keyEvents: SpectacleKeyEvent[];
  maxHp: number;
}

export interface SpectacleResult {
  total: number;        // 0-100 加权总分
  dimensions: {
    damageExchange: number;    // 0-100
    movementComplexity: number; // 0-100
    coverInteraction: number;   // 0-100
    phaseProgression: number;   // 0-100
    durationOptimal: number;    // 0-100
    lowHpComeback: number;      // 0-100
  };
}

// ===== 权重 =====
const W_DAMAGE_EXCHANGE = 0.25;
const W_MOVEMENT = 0.20;
const W_COVER = 0.15;
const W_PHASE = 0.15;
const W_DURATION = 0.15;
const W_COMEBACK = 0.10;

// ===== 核心计算 =====

/**
 * 计算观赏性评分
 */
export function computeSpectacle(input: SpectacleInput): SpectacleResult {
  const { frames, totalTicks, keyEvents, maxHp } = input;

  const damageExchange = scoreDamageExchange(frames);
  const movementComplexity = scoreMovementComplexity(frames);
  const coverInteraction = scoreCoverInteraction(keyEvents);
  const phaseProgression = scorePhaseProgression(frames, maxHp);
  const durationOptimal = scoreDurationOptimal(totalTicks);
  const lowHpComeback = scoreLowHpComeback(frames, maxHp);

  const total =
    damageExchange * W_DAMAGE_EXCHANGE +
    movementComplexity * W_MOVEMENT +
    coverInteraction * W_COVER +
    phaseProgression * W_PHASE +
    durationOptimal * W_DURATION +
    lowHpComeback * W_COMEBACK;

  return {
    total: Math.round(total * 10) / 10,
    dimensions: {
      damageExchange,
      movementComplexity,
      coverInteraction,
      phaseProgression,
      durationOptimal,
      lowHpComeback,
    },
  };
}

// ===== 维度 1: 伤害交换率 =====
// 双方都有伤害输出 → 高分（激烈对射）
// 一方碾压（只有一方输出）→ 低分
function scoreDamageExchange(frames: SpectacleFrame[]): number {
  let aDealt = 0;
  let bDealt = 0;
  let exchangeTicks = 0; // 双方在同一 tick 或相近 tick 都有伤害

  for (const f of frames) {
    if (f.damageAtoB > 0) aDealt += f.damageAtoB;
    if (f.damageBtoA > 0) bDealt += f.damageBtoA;
    if (f.damageAtoB > 0 && f.damageBtoA > 0) exchangeTicks++;
  }

  // 无伤害 → 0 分
  if (aDealt + bDealt === 0) return 0;

  // 伤害对称性：越对称越高（0-1）
  const total = aDealt + bDealt;
  const symmetry = 1 - Math.abs(aDealt - bDealt) / total;

  // 交换频率：双方同时有伤害的 tick 占比
  const exchangeRate = frames.length > 0 ? exchangeTicks / frames.length : 0;

  // 总伤害量：伤害越多越精彩（归一化到 maxHp*2）
  const damageVolume = Math.min(1, total / (150 * 2));

  const score = (symmetry * 0.4 + exchangeRate * 3 * 0.3 + damageVolume * 0.3) * 100;
  return clamp(score, 0, 100);
}

// ===== 维度 2: 移动复杂度 =====
// 移动次数 + 位置多样性（不走一步不挪窝的蹲坑局 = 低分）
function scoreMovementComplexity(frames: SpectacleFrame[]): number {
  // 空回放 → 0 分（防止除零 NaN）
  if (frames.length === 0) return 0;

  let aMoves = 0;
  let bMoves = 0;
  const aPositions = new Set<string>();
  const bPositions = new Set<string>();

  for (const f of frames) {
    if (f.selfAction === "move") aMoves++;
    if (f.enemyAction === "move") bMoves++;
    aPositions.add(`${f.selfPos.x},${f.selfPos.y}`);
    bPositions.add(`${f.enemyPos.x},${f.enemyPos.y}`);
  }

  const totalMoves = aMoves + bMoves;
  const totalPositions = aPositions.size + bPositions.size;

  // 移动频率：每 50 tick 至少移动 1 次 = 及格
  const moveFrequency = Math.min(1, totalMoves / (frames.length / 50));

  // 位置多样性：去重位置数 / 总 tick（归一化）
  const positionDiversity = Math.min(1, totalPositions / 20);

  const score = (moveFrequency * 0.5 + positionDiversity * 0.5) * 100;
  return clamp(score, 0, 100);
}

// ===== 维度 3: 掩体交互 =====
// COVER_USE 事件次数越多越好（利用地形）
function scoreCoverInteraction(keyEvents: SpectacleKeyEvent[]): number {
  const coverUses = keyEvents.filter((e) => e.eventType === "COVER_USE").length;
  // 0 次 = 0 分, 1 次 = 40, 2 次 = 70, 3+ 次 = 100
  if (coverUses === 0) return 0;
  if (coverUses === 1) return 40;
  if (coverUses === 2) return 70;
  return 100;
}

// ===== 维度 4: 阶段推进 =====
// HP 阶段: full(>66%) → mid(33-66%) → low(<33%) → death
// 经历的阶段越多越精彩（开局秒杀 = 1 阶段 = 低分）
function scorePhaseProgression(frames: SpectacleFrame[], maxHp: number): number {
  if (frames.length === 0) return 0;

  const phases = new Set<string>();
  let aPhase = "full";
  let bPhase = "full";

  for (const f of frames) {
    const aRatio = f.selfHp / maxHp;
    const bRatio = f.enemyHp / maxHp;

    const newAPhase = aRatio > 0.66 ? "full" : aRatio > 0.33 ? "mid" : aRatio > 0 ? "low" : "death";
    const newBPhase = bRatio > 0.66 ? "full" : bRatio > 0.33 ? "mid" : bRatio > 0 ? "low" : "death";

    if (newAPhase !== aPhase) {
      phases.add(`a_${newAPhase}`);
      aPhase = newAPhase;
    }
    if (newBPhase !== bPhase) {
      phases.add(`b_${newBPhase}`);
      bPhase = newBPhase;
    }
  }

  // 阶段数: 2 (full→death) = 33, 4 = 67, 6+ = 100
  const phaseCount = phases.size;
  const score = Math.min(100, (phaseCount / 6) * 100);
  return clamp(score, 0, 100);
}

// ===== 维度 5: 时长最优 =====
// 200-800 ticks 黄金区间 = 100 分
// <100 ticks (秒杀) 或 >1500 ticks (僵局) = 低分
function scoreDurationOptimal(totalTicks: number): number {
  if (totalTicks >= 200 && totalTicks <= 800) return 100;
  if (totalTicks < 100) return 10;
  if (totalTicks < 200) return 60;
  if (totalTicks > 1500) return 20;
  if (totalTicks > 1200) return 50;
  if (totalTicks > 800) return 80;
  return 60;
}

// ===== 维度 6: 濒死翻盘 =====
// 任一方 HP 降到 30% 以下后存活或翻盘
function scoreLowHpComeback(frames: SpectacleFrame[], maxHp: number): number {
  if (frames.length === 0) return 0;

  const lowThreshold = maxHp * 0.3;
  let aWasLow = false;
  let bWasLow = false;
  let aFinalHp = 0;
  let bFinalHp = 0;

  for (const f of frames) {
    if (f.selfHp <= lowThreshold && f.selfHp > 0) aWasLow = true;
    if (f.enemyHp <= lowThreshold && f.enemyHp > 0) bWasLow = true;
    aFinalHp = f.selfHp;
    bFinalHp = f.enemyHp;
  }

  let score = 0;
  // A 濒死后存活
  if (aWasLow && aFinalHp > 0) score = Math.max(score, 80);
  // B 濒死后存活
  if (bWasLow && bFinalHp > 0) score = Math.max(score, 80);
  // A 濒死后反杀（B 死了）
  if (aWasLow && bFinalHp <= 0) score = 100;
  // B 濒死后反杀
  if (bWasLow && aFinalHp <= 0) score = 100;

  return score;
}

// ===== 工具 =====
function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}
